/**
 * Live-typing sanitizer for a file base name (no extension): lowercase, spaces → hyphen,
 * drops anything outside `[a-z0-9._-]`. Keeps a trailing hyphen so the user can keep typing
 * a multi-word name ("banner " → "banner-" → "banner-hero").
 */
export const sanitizeBaseNameInput = (value: string): string =>
  value
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9._-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^[-.]+/, '');

/** Final base name: live sanitizer plus trimmed edges and a non-empty fallback. */
export const finalizeBaseName = (value: string): string => sanitizeBaseNameInput(value).replace(/[-.]+$/, '') || 'file';

/** Split `name.ext` into base and extension (extension without the dot, may be empty). */
export const splitFileName = (name: string): { base: string; ext: string } => {
  const dotIndex = name.lastIndexOf('.');
  return dotIndex > 0 ? { base: name.slice(0, dotIndex), ext: name.slice(dotIndex + 1) } : { base: name, ext: '' };
};

/**
 * Sanitize a full file name (`My Photo.PNG` → `my-photo.png`). Applied before the R2 key is
 * built — never percent-encode afterwards, see `toStoredPath` in process-handler.ts.
 */
export const sanitizeFileName = (name: string): string => {
  const { base, ext } = splitFileName(name);
  const cleanExt = ext.toLowerCase().replace(/[^a-z0-9]/g, '');
  const cleanBase = finalizeBaseName(base);
  return cleanExt ? `${cleanBase}.${cleanExt}` : cleanBase;
};
